import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import { Box, Center, Flex, IconButton, Link } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import React, { FC, useState } from 'react';
import DataTable from '../components/Table/DataTable';
import StatusChip from '../components/Table/StatusChip';
import TableFilters from '../components/Table/Filters';
import { convertISODateToDateTime } from '../lib/helpers';
import SearchPageBannerSvg from '../assets/SearchPageBannerSvg';
import { exportSearchResultsToCsv, fetchEnrollmentRecords } from '../services/apiService';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export interface ISearchTableRow {
    capacity: string;
    color: string;
    dateTime: string;
    duration: string;
    lastStep: string;
    make: string;
    method: string;
    model: string;
    partner: string;
    program: string;
    recordId: string;
    reviewStatus: string;
    reviewer: string;
    submissionStatus: string;
    deviceType: string;
    // inserted thru UI pre processing
    primaryMdn: string;
    secondaryMdn: string;
    sessionCount: number;
    subRows?: ISearchTableRow[];
    imei?: string;
    companyName?: string;
    grade?: string;
    gradedPrice?: string;
    tradeNumber?: string;
    dealerCode?: string;
}

export type FailKeys = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;
export type Grade = 'A' | 'B' | 'C+' | 'C-' | 'D' | 'E' | `F${FailKeys}` | 'ELIGIBLE';

const GWO_GRADE: Grade[] = ['A', 'B', 'C+', 'ELIGIBLE'];

const columnHelper = createColumnHelper<ISearchTableRow>();

const SearchPage: FC<{ isTradeIn?: boolean }> = ({ isTradeIn }) => {
    // Shared filter states
    const todayDate = new Date();
    const defaultDates = {
        startDate: new Date(todayDate.setHours(0, 0, 0, 0)),
        endDate: new Date(todayDate.setHours(23, 59, 59, 999)),
    };
    const [startDate, setStartDate] = useState(defaultDates.startDate);
    const [endDate, setEndDate] = useState(defaultDates.endDate);

    const mdnTypes = ['Phone to Enroll No.', 'Subscriber No.'];
    const [selectedMdnType, setSelectedMdnType] = useState<string>(mdnTypes[1]);

    const defaultFilterForm = {
        startTime: new Date(todayDate.setHours(0, 0, 0, 0)).toISOString(),
        endTime: new Date(todayDate.setHours(23, 59, 59, 999)).toISOString(),
        mdnType: selectedMdnType === mdnTypes[0] ? 'secondary' : 'primary',
        partner: !isTradeIn ? ['M1', 'Singtel', 'StarHub'] : ['Asurion'],
    };

    const [filterForm, setFilterForm] = useState(defaultFilterForm);
    const [filterFormData, setFilterFormData] = useState(defaultFilterForm);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Pagination
    const [pageToken, setPageToken] = useState('');
    const [pageNum, setPageNum] = useState(1);
    const [rowLimit, setRowLimit] = useState(10);

    const columns = React.useMemo(() => {
        return [
            ...(!isTradeIn
                ? [
                      columnHelper.accessor('sessionCount', {
                          cell: ({ row, getValue }) =>
                              getValue() > 1 ? (
                                  <Center>
                                      <IconButton
                                          aria-label="add session"
                                          bgColor="black"
                                          icon={
                                              row.getIsExpanded() ? (
                                                  <MinusIcon
                                                      color="white"
                                                      w="10px"
                                                      h="10px"
                                                  />
                                              ) : (
                                                  <AddIcon
                                                      color="white"
                                                      w="10px"
                                                      h="10px"
                                                  />
                                              )
                                          }
                                          borderRadius="50%"
                                          minH="35px"
                                          minW="35px"
                                          h="16px"
                                          w="16px"
                                          onClick={() => row.toggleExpanded()}
                                      />
                                      <Box pl="8px" fontSize="16px">
                                          {getValue()}
                                      </Box>
                                  </Center>
                              ) : (
                                  <></>
                              ),
                          header: 'Session',
                      }),

                      columnHelper.accessor('secondaryMdn', {
                          cell: ({ row, getValue }) =>
                              row.original.sessionCount > 0 ? (
                                  <Flex justifyContent="center">
                                      <Link
                                          href={`/enrollment/${row.original.primaryMdn.replaceAll(
                                              '+',
                                              ''
                                          )}/${String(getValue()).replaceAll('+', '')}`}
                                      >
                                          {getValue()}
                                      </Link>
                                  </Flex>
                              ) : (
                                  <></>
                              ),
                          header: 'Phone to Enroll No.',
                      }),

                      columnHelper.accessor('primaryMdn', {
                          cell: ({ getValue }) => <Box>{getValue()}</Box>,
                          header: 'Subscriber No.',
                      }),
                  ]
                : []),

            ...(isTradeIn
                ? [
                      columnHelper.accessor('imei', {
                          cell: ({ row, getValue }) =>
                              row.original.sessionCount > 0 ? (
                                  <Flex justifyContent="center">
                                      <Link
                                          href={`/enrollment/${row.original.primaryMdn.replaceAll(
                                              '+',
                                              ''
                                          )}/${String(row.original.secondaryMdn).replaceAll('+', '')}`}
                                      >
                                          {getValue() || 'View Details'}
                                      </Link>
                                  </Flex>
                              ) : (
                                  <></>
                              ),
                          header: 'IMEI',
                      }),
                  ]
                : []),

            columnHelper.accessor('dateTime', {
                cell: info =>
                    info.getValue() !== '' ? (
                        <Box w="90px">{convertISODateToDateTime(info.getValue())}</Box>
                    ) : (
                        <></>
                    ),
                header: 'Date & Time',
            }),
            columnHelper.accessor(row => `${row.submissionStatus} ${row.lastStep}`, {
                id: 'submissionStatus',
                cell: ({ row }) =>
                    row.original.submissionStatus !== 'complete' ? (
                        <StatusChip
                            status="INCOMPLETE"
                            customText={row.original.lastStep}
                        />
                    ) : (
                        <StatusChip status="COMPLETE" />
                    ),
                header: 'Submission Status',
            }),
            columnHelper.accessor(row => `${row.deviceType}`, {
                id: 'deviceType',
                cell: ({ row }) => (
                    <Box textTransform="capitalize" maxW="200px">
                        {row.original.deviceType}
                    </Box>
                ),
                header: 'Device Type',
            }),
            columnHelper.accessor(
                row => `${row.make} ${row.model}, ${row.capacity}, ${row.color}`,
                {
                    cell: ({ row }) => (
                        <>
                            {`${row.original.make || ''} ${row.original.model || ''}`}{' '}
                            <br />
                            {`${row.original.capacity || ''} ${row.original.color || ''}`}
                        </>
                    ),
                    header: 'Model',
                }
            ),
            ...(isTradeIn
                ? [
                      columnHelper.accessor('tradeNumber', {
                          cell: ({ getValue }) => <Box>{getValue()}</Box>,
                          header: 'Trade ID',
                      }),
                      columnHelper.accessor('grade', {
                          cell: ({ row }) => {
                              const grade = row.original.grade as Grade;
                              const submissionStatus = row.original.submissionStatus;
                              const program = row.original.program;
                              return submissionStatus === 'complete' &&
                                  program === 'tradein' ? (
                                  GWO_GRADE.includes(grade) ? (
                                      <StatusChip status="COMPLETE" customText={'GWO'} />
                                  ) : (
                                      <StatusChip status="REJECTED" customText={'NGWO'} />
                                  )
                              ) : (
                                  '-'
                              );
                          },
                          header: 'Device Grade',
                      }),
                      columnHelper.accessor('gradedPrice', {
                          id: 'gradedPrice',
                          cell: ({ row }) => {
                              const gradedPrice = row.original.gradedPrice;
                              const submissionStatus = row.original.submissionStatus;

                              return (
                                  <Box>
                                      {gradedPrice != null &&
                                      typeof gradedPrice === 'number' &&
                                      submissionStatus === 'complete'
                                          ? `RM ${gradedPrice}`
                                          : '-'}
                                  </Box>
                              );
                          },
                          header: 'Device Value',
                      }),
                  ]
                : []),

            columnHelper.accessor(row => `${row.partner} ${row.program}`, {
                id: 'partnerPlan',
                cell: ({ row }) => (
                    <Box textTransform="capitalize" maxW="200px">
                        {row.original.partner}
                        <br />
                        {row.original.program}
                    </Box>
                ),
                header: 'Partner & Plan',
            }),
            ...(isTradeIn
                ? [
                      columnHelper.accessor('dealerCode', {
                          cell: ({ getValue }) => <Box>{getValue()?.toUpperCase()}</Box>,
                          header: 'Dealer Code',
                      }),
                      columnHelper.accessor('companyName', {
                          cell: ({ getValue }) => <Box>{getValue()}</Box>,
                          header: 'Location',
                      }),
                  ]
                : []),

            columnHelper.accessor(row => `${row.method} ${row.duration}`, {
                id: 'methodDuration',
                cell: ({ row }) => (
                    <>
                        {`${row.original.method}`} <br /> {`${row.original.duration}`}
                    </>
                ),
                header: 'Method & Duration',
            }),

            columnHelper.accessor('reviewStatus', {
                id: 'reviewStatus',
                cell: info => (
                    <StatusChip
                        status={
                            info.getValue()
                                ? info.getValue().toUpperCase().replaceAll(' ', '_')
                                : 'UNDEFINED'
                        }
                    />
                ),
                header: 'Review Status',
            }),
            ...(!isTradeIn
                ? [
                      columnHelper.accessor('reviewer', {
                          id: 'reviewer',
                          cell: info => info.getValue(),
                          header: 'Reviewer',
                      }),
                  ]
                : []),
        ];
    }, [isTradeIn]);

    const downloadBlob = (content, fileName, contentType) => {
        const blob = new Blob([content], { type: contentType });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        link.click();
    };

    const handleExport = async () => {
        setIsLoading(true);
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const csvData = await exportSearchResultsToCsv(filterFormData);
        const updatedCsvData = updateTimestampsInCsv(csvData, localTimeZone);
        let jsonData = updatedCsvData
            .split('\n')
            .slice(1)
            .map(line => {
                const values = line.split(',');
                return {
                    'Smart Scan ID': values[0],
                    'Phone to Enroll No.': values[1],
                    'Subscriber No.': values[2],
                    'Date & Time': values[3],
                    'Submission Status': values[4],
                    Model: values[5],
                    Partner: values[6],
                    Plan: values[7],
                    Method: values[8],
                    Location: values[9],
                    'Dealer Code': values[10],
                    IMEI: values[11],
                    'Trade ID': values[12],
                    'Device Value': values[13],
                    'Device Grade': values[14],
                    Duration: values[15],
                    'Review Status': values[16],
                    Reviewer: values[17],
                };
            });

        const headers = [
            'Smart Scan ID',
            ...(!isTradeIn ? ['Phone to Enroll No.', 'Subscriber No.'] : []),
            'Date & Time',
            'Submission Status',
            'Model',
            'Partner',
            'Plan',
            'Method',
            ...(isTradeIn
                ? [
                      'IMEI',
                      'Location',
                      'Dealer Code',
                      'Trade ID',
                      'Device Value',
                      'Device Grade',
                  ]
                : []),
            'Duration',
            'Review Status',
            ...(!isTradeIn ? ['Reviewer'] : []),
        ];

        const rows = jsonData.map(row => {
            return [
                row['Smart Scan ID'],
                ...(!isTradeIn
                    ? [row['Phone to Enroll No.'] || '', row['Subscriber No.'] || '']
                    : []),
                row['Date & Time'],
                row['Submission Status'],
                row['Model'],
                row['Partner'],
                row['Plan'],
                row['Method'],
                ...(isTradeIn
                    ? [
                          row['IMEI'] || '',
                          row['Location'] || '',
                          row['Dealer Code'] || '',
                          row['Trade ID'] || '',
                          row['Device Value'] || '',
                          row['Device Grade'] || '',
                      ]
                    : []),
                row['Duration'],
                row['Review Status'],
                ...(!isTradeIn ? [row['Reviewer'] || ''] : []),
            ];
        });

        const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

        downloadBlob(csvContent, 'search-export.csv', 'text/csv;charset=utf-8;');
        setIsLoading(false);
    };

    const updateTimestampsInCsv = (csvData, timezone) => {
        const lines = csvData.split('\n');
        const header = lines[0];
        const dataRows = lines.slice(1);

        const updatedDataRows = dataRows.map(row => {
            const columns = row.split(',');
            const rawDate = columns[3];
            const localDate = toZonedTime(parseISO(rawDate), timezone);
            const formattedDate = format(localDate, "yyyy-MM-dd'T'HH:mm:ss.SSSX");
            columns[3] = formattedDate;
            return columns.join(',');
        });
        return [header, ...updatedDataRows].join('\n');
    };

    const useTableData = () => {
        return useQuery(['session', rowLimit, pageToken, filterFormData], () =>
            fetchEnrollmentRecords(rowLimit, pageToken, filterFormData)
        );
    };

    const {
        data: tableData,
        error: errorTableData,
        isFetching: isFetchingTableData,
        refetch: refetchTableData,
    } = useTableData();

    return (
        <Box
            id="search-page"
            backgroundColor="asuWhite.200"
            h="fit-content"
            w="100%"
            boxSizing="border-box"
            pt="60px"
        >
            <Box position="absolute" width="100%" zIndex={1}>
                <Flex width="inherit">
                    <SearchPageBannerSvg style={{ margin: 'auto' }} />
                </Flex>
            </Box>
            <Flex
                position="relative"
                direction="column"
                boxSizing="border-box"
                backgroundColor="asuWhite.200"
                p="80px 12px 25px"
                w="100%"
                h="fit-content"
                border="none"
            >
                <TableFilters
                    filterForm={filterForm}
                    setFilterForm={setFilterForm}
                    setIsFormSubmitted={setIsFormSubmitted}
                    setFilterFormData={setFilterFormData}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    setPageToken={setPageToken}
                    setPageNum={setPageNum}
                    defaultFilterForm={defaultFilterForm}
                    defaultDates={defaultDates}
                    mdnTypes={mdnTypes}
                    selectedMdnType={selectedMdnType}
                    setSelectedMdnType={setSelectedMdnType}
                    handleExport={handleExport}
                    refetchTableData={refetchTableData}
                    isTradeIn={isTradeIn}
                    isLoading={isLoading}
                />
                <Box
                    w="100%"
                    h="fit-content"
                    backgroundColor="asuWhite.200"
                    m="95px auto 0"
                >
                    <Box w="100%" h="fit-content" backgroundColor="white">
                        <DataTable
                            tableData={tableData}
                            errorTableData={errorTableData}
                            isFetchingTableData={isFetchingTableData}
                            columns={columns}
                            pageNum={pageNum}
                            setPageNum={setPageNum}
                            setPageToken={setPageToken}
                            rowLimit={rowLimit}
                            setRowLimit={setRowLimit}
                        />
                    </Box>
                </Box>
            </Flex>
        </Box>
    );
};

export default SearchPage;
